import { useCallback, useRef, useMemo, useState } from 'react';
import { Button, Space, Tag, Modal, message, Select, Alert } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getSnapshots,
  deleteSnapshot,
  generateSnapshot,
  SNAPSHOT_TYPES,
  SNAPSHOT_ENTITY_TYPES,
  type AnalyticsSnapshot,
  type SnapshotFilters,
} from '@/api/analyticsApi';
import { useAuth } from '@/hooks/useAuth';
import AnalyticsNav from './AnalyticsNav';

export default function SnapshotListPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [entityTypeFilter, setEntityTypeFilter] = useState<string | undefined>();
  const [snapshotTypeFilter, setSnapshotTypeFilter] = useState<string | undefined>();
  const [generating, setGenerating] = useState(false);
  const [genSnapshotType, setGenSnapshotType] = useState<string>('daily');
  const [genEntityType, setGenEntityType] = useState<string>('overall');

  const canRead = hasPermission('analytics', 'read');
  const canWrite = hasPermission('analytics', 'write');
  const canDelete = hasPermission('analytics', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: SnapshotFilters = { ...params };
      if (entityTypeFilter) filters.entity_type = entityTypeFilter;
      if (snapshotTypeFilter) filters.snapshot_type = snapshotTypeFilter;
      return getSnapshots(filters);
    },
    [entityTypeFilter, snapshotTypeFilter]
  );

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      await generateSnapshot({
        snapshot_type: genSnapshotType,
        entity_type: genEntityType,
      });
      message.success(t('analytics_snapshot.generate_success'));
      gridRef.current?.reload();
    } catch {
      message.error(t('app.error'));
    } finally {
      setGenerating(false);
    }
  }, [genSnapshotType, genEntityType, t]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('analytics_snapshot.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteSnapshot(id);
            message.success(t('analytics_snapshot.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<AnalyticsSnapshot> = useMemo(
    () => [
      {
        title: t('analytics_snapshot.field.snapshot_type'),
        dataIndex: 'snapshot_type',
        key: 'snapshot_type',
        width: 110,
        render: (v: string) => (
          <Tag color="purple">{t(`analytics_snapshot.snapshot_type.${v}`, v)}</Tag>
        ),
      },
      {
        title: t('analytics_snapshot.field.entity_type'),
        dataIndex: 'entity_type',
        key: 'entity_type',
        width: 120,
        render: (v: string) => (
          <Tag color="blue">{t(`analytics_snapshot.entity_type.${v}`, v)}</Tag>
        ),
      },
      {
        title: t('analytics_snapshot.field.snapshot_date'),
        dataIndex: 'snapshot_date',
        key: 'snapshot_date',
        width: 160,
        sorter: true,
        render: (v: string) => v ? new Date(v).toLocaleString() : '-',
      },
      {
        title: t('analytics_snapshot.field.period_start'),
        dataIndex: 'period_start',
        key: 'period_start',
        width: 120,
        render: (v: string) => v ? new Date(v).toLocaleDateString() : '-',
      },
      {
        title: t('analytics_snapshot.field.period_end'),
        dataIndex: 'period_end',
        key: 'period_end',
        width: 120,
        render: (v: string) => v ? new Date(v).toLocaleDateString() : '-',
      },
      {
        title: t('analytics_snapshot.field.summary'),
        dataIndex: 'summary',
        key: 'summary',
        ellipsis: true,
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 80,
        render: (_: unknown, record: AnalyticsSnapshot) => (
          <Space size="small">
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, canDelete, handleDelete]
  );

  const toolbar = (
    <Space wrap>
      <Select
        allowClear
        placeholder={t('analytics_snapshot.field.snapshot_type')}
        value={snapshotTypeFilter}
        onChange={(v) => { setSnapshotTypeFilter(v); gridRef.current?.reload(); }}
        style={{ width: 140 }}
        options={SNAPSHOT_TYPES.map((st) => ({
          value: st,
          label: t(`analytics_snapshot.snapshot_type.${st}`),
        }))}
      />
      <Select
        allowClear
        placeholder={t('analytics_snapshot.field.entity_type')}
        value={entityTypeFilter}
        onChange={(v) => { setEntityTypeFilter(v); gridRef.current?.reload(); }}
        style={{ width: 140 }}
        options={SNAPSHOT_ENTITY_TYPES.map((et) => ({
          value: et,
          label: t(`analytics_snapshot.entity_type.${et}`),
        }))}
      />
      {canWrite && (
        <Space>
          <Select
            value={genSnapshotType}
            onChange={setGenSnapshotType}
            style={{ width: 120 }}
            options={SNAPSHOT_TYPES.map((st) => ({
              value: st,
              label: t(`analytics_snapshot.snapshot_type.${st}`),
            }))}
          />
          <Select
            value={genEntityType}
            onChange={setGenEntityType}
            style={{ width: 120 }}
            options={SNAPSHOT_ENTITY_TYPES.map((et) => ({
              value: et,
              label: t(`analytics_snapshot.entity_type.${et}`),
            }))}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            loading={generating}
            onClick={handleGenerate}
          >
            {t('analytics_snapshot.generate')}
          </Button>
        </Space>
      )}
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <AnalyticsNav />
      <h2>{t('analytics_snapshot.title')}</h2>
      <DataGrid<AnalyticsSnapshot>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('analytics_snapshot.search_placeholder')}
        defaultSortField="snapshot_date"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
